import { useCallback, type ReactNode } from 'react';
import { FeatureFlagsProvider } from '@vaptmenu/feature-flags-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import {
  getAccessToken,
  getSelectedTenantId,
  renewAfterUnauthorized,
} from '@/shared/lib/auth-coordinator';

const ENDPOINT = '/api/v1/panel/feature-flags';

/**
 * Wraps the generic FeatureFlagsProvider with the panel's auth context.
 *
 * The `/panel/feature-flags` endpoint requires a tenant Bearer token, but the
 * library's default fetch sends only cookies + X-Anonymous-Id (no Authorization)
 * — which yielded 401 in staging. Here we:
 *   - inject `Authorization: Bearer <accessToken>` (+ `X-Tenant-Id`) from the
 *     auth store via a stable fetcher;
 *   - renew an expired token on 401 through the auth coordinator, the same
 *     single in-flight renewal the axios client and the Vapt IA stream use;
 *   - only mount the provider once authenticated (accessToken + selectedTenant),
 *     so there is no unauthenticated request before login;
 *   - key the provider by tenant id so flags are re-fetched on tenant switch.
 *
 * Before authentication, children render without a provider — `useFeatureFlag`
 * returns false (feature hidden), which is the safe default.
 */
export function PanelFeatureFlagsProvider({ children }: { children: ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const selectedTenant = useAuthStore((s) => s.selectedTenant);

  // Stable fetcher: reads the latest token/tenant from the store on each call,
  // so its identity never changes (avoids the provider's refetch-on-every-render).
  const fetcher = useCallback<typeof fetch>(async (input, init) => {
    const send = (token: string | null) => {
      const headers = new Headers(init?.headers);
      const tenantId = getSelectedTenantId();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      if (tenantId) headers.set('X-Tenant-Id', tenantId);
      // Fora do cache HTTP do navegador. A rota responde `Cache-Control:
      // max-age=60` sem `Vary: Authorization`, e o navegador guarda pela URL:
      // quem trocava de loja, ou saía e entrava com outra conta, em menos de um
      // minuto recebia as flags da loja anterior, e a remontagem por `key` não
      // adiantava. A consulta já é espaçada (5 min); o cache não poupava nada.
      return fetch(input, { ...init, headers, cache: 'no-store' });
    };

    const sentToken = getAccessToken();
    const response = await send(sentToken);
    if (response.status !== 401) return response;

    // 401 com o token de 15 minutos vencido: renova pelo coordenador e repete
    // uma vez. Sem isso, a página aberta com o token salvo já vencido (aba
    // recarregada, restaurada pelo navegador ou recarregada depois de um
    // deploy) lia as flags antes da renovação feita pelas outras chamadas e
    // ficava sem nenhuma — Impressão e Vapt IA escondidas — até a consulta
    // seguinte, 5 minutos depois. Diferente do backoffice, o painel não tem
    // janela de inatividade para proteger: as consultas periódicas de pedidos
    // e de impressão já renovam pelo axios, do mesmo jeito.
    const renewed = await renewAfterUnauthorized(sentToken);
    return renewed ? send(renewed) : response;
  }, []);

  if (!accessToken || !selectedTenant) {
    return <>{children}</>;
  }

  return (
    <FeatureFlagsProvider key={selectedTenant.id} endpoint={ENDPOINT} fetcher={fetcher}>
      {children}
    </FeatureFlagsProvider>
  );
}
